import { Core } from "@faire/design-tokens";
import { OwFErrorMessage } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFErrorMessage";
import { OwFFormSection } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormSection";
import { useIsNMIApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useOwFForm } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFForm";
import { useOwFSectionTitles } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFSectionTitles";
import { Privacy } from "@faire/retailer-visitor-shared/components/Privacy";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { assignSettingOwFSoftCreditCheckFlow } from "@faire/retailer-visitor-shared/settings/getSettingOwFSoftCreditCheckFlow";
import { useSettingSkipOwFUpsell } from "@faire/retailer-visitor-shared/settings/useSettingSkipOwFUpsell";
import { useOpenWithFaireApplicationStatus } from "@faire/retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireApplicationStatus";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IRetailerLaunchWithFaire } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaire";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import React, { FC, useEffect, useMemo } from "react";
import styled from "styled-components";

import { CreditHistoryPrivacy } from "../../../views/scc-shared/CreditHistoryPrivacy";

import { useOwFInitializer, useOwFStore } from "./__internal__/OwFFormStore";
import { useIsOptimizedSCC } from "./useIsOptimizedSCC";

interface IOwFFormProps {
  startingValues?: Partial<IRetailerLaunchWithFaireApplication>;
  isSignUp?: boolean;
  afterSubmit?: () => void;
  onSkip?: () => void;
}
export const OwFForm: FC<IOwFFormProps> = ({
  startingValues,
  isSignUp,
  afterSubmit,
  onSkip,
}) => {
  useOwFInitializer(startingValues, isSignUp);

  const { status } = useOpenWithFaireApplicationStatus();
  const { isAmerican } = useNationality();
  const isOptimizedSCC = useIsOptimizedSCC();
  const {
    basicInformation,
    storeInformation,
    financialInformation,
    additionalInformation,
  } = useOwFSectionTitles();
  const viewState = useOwFForm({ startingValues, isSignUp });
  const { isNMIApplication } = useIsNMIApplication();
  const skipOwFUpsell = useSettingSkipOwFUpsell();
  const isSignUpAndSkipUpsell = isSignUp && skipOwFUpsell;

  const loading = useOwFStore((state) => state.loading);

  const {
    basicInfoFields,
    storeInfoFields,
    financialInfoFields,
    additionalInfoFields,
    errors,
    submit,
  } = viewState;

  useEffect(() => {
    if (status === IRetailerLaunchWithFaire.Status.ELIGIBLE && isAmerican) {
      assignSettingOwFSoftCreditCheckFlow();
    }
  }, [status, isAmerican]);

  const handleSubmit = async () => {
    const submitStatus = await submit();
    if (submitStatus && afterSubmit) {
      return afterSubmit();
    }
  };

  const maxFormWidth = useMemo(() => {
    if (isNMIApplication) {
      return "440px";
    }
    if (isSignUpAndSkipUpsell) {
      return "720px";
    }

    return "420px";
  }, [isNMIApplication, isSignUp]);

  return (
    <FormWrapper
      style={{
        maxWidth: maxFormWidth,
      }}
    >
      {loading ? (
        <Column align="center">
          <Spacer height="2x" />
          <LoadingSpinner />
        </Column>
      ) : (
        <>
          <OwFFormSection
            title={basicInformation}
            formFields={basicInfoFields}
            viewState={viewState}
            hideBorders={isSignUpAndSkipUpsell}
          />
          <OwFFormSection
            title={storeInformation}
            formFields={storeInfoFields}
            viewState={viewState}
            hideBorders={isSignUpAndSkipUpsell}
          />
          <OwFFormSection
            title={financialInformation}
            formFields={financialInfoFields}
            viewState={viewState}
            hideBorders={isSignUpAndSkipUpsell}
          />
          <OwFFormSection
            title={additionalInformation}
            formFields={additionalInfoFields}
            viewState={viewState}
            hideBorders={isSignUpAndSkipUpsell}
            optional
          />
          <FullWidthButton
            onClick={handleSubmit}
            data-test-id="owf-submit-application"
          >
            <StrictLocalMsg
              defaultMessage="Submit"
              description={{
                text: "Caption of a button to submit application",
              }}
            />
          </FullWidthButton>
          <OwFErrorMessage errors={errors} />
          {onSkip ? (
            <>
              <Spacer height="2x" />
              <Button
                variant="tertiary"
                onClick={onSkip}
                data-test-id="skip-for-now"
              >
                <StrictLocalMsg
                  defaultMessage="Skip for now"
                  description={{
                    text: "Give retailer option to skip the application",
                    img: "https://cdn.faire.com/fastly/c1c468924d050f0d356554e9aacb8b10b19768e5a767ed7c4b837e1e244906dc.png",
                  }}
                />
              </Button>
            </>
          ) : null}
          <Spacer height="2x" />
          {isOptimizedSCC ? (
            <CreditHistoryPrivacy />
          ) : (
            <Privacy color={Core.surface.default} />
          )}
        </>
      )}
    </FormWrapper>
  );
};

const FormWrapper = styled(Column)`
  width: 100%;
`;

const FullWidthButton = styled(Button)`
  width: 100%;
`;
